<template>
  <WdsModal :show :title="$t('Create Webhook')" @cancel="show = false">
    <WebhookModalForm
      v-model:input="input"
      :loading
      :validation-errors="validationErrors"
      data-test="modal-create-webhook"
    />
    <template #footer>
      <WdsStack justify-space-between>
        <WdsButton :disabled="loading" @click="show = false">{{ $t('Cancel') }}</WdsButton>
        <WdsStack gap-md justify-center align-center class="wds-footer-action-buttons">
          <WdsToggle v-model:checked="input.isActive" data-test="modal-webhook-active">
            {{ $t('Set as active') }}
          </WdsToggle>
          <WdsButton primary :disabled="loading" data-test="modal-webhook-submit" @click="submit">
            {{ $t('Confirm') }}
          </WdsButton>
        </WdsStack>
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script setup lang="ts">
import { WdsButton, WdsModal, WdsStack, WdsToggle } from '@worksome/wds-components'
import { reactive, watch } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { required, requiredIf, url } from '@vuelidate/validators'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { createWebhookMutation } from '@/components/Integrations/Webhooks/graphql/mutations'
import NetworkError from '@/utils/notification/NetworkError'
import WebhookModalForm from '@/components/Integrations/Webhooks/WebhookModalForm.vue'
import useApolloError from '@/composables/useApolloError'
import useCustomVuelidate from '@/composables/useCustomVuelidate'
import useApolloAndVuelidate from '@/composables/useApolloAndVuelidate'
import { WebhookEventType } from '@/gql/graphql'
import { SUPPLIER_WEBHOOK_EVENT_TYPES } from '@/components/Integrations/Webhooks/types'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'

const show = defineModel<boolean>('show')

const clientId = 'publicApi'

const { isRecruiter } = useAccountCapabilities()

interface CreateWebhookInput {
  title: string
  description?: string | null
  url: string
  secret?: string | null
  isActive: boolean
  subscribedEvents?: WebhookEventType[] | null
  clientId?: string | null
  clientSecret?: string | null
  clientUrl?: string | null
  account: string
}

const input = reactive<CreateWebhookInput>({
  title: '',
  description: null,
  url: '',
  secret: null,
  isActive: false,
  // null means all events for a company, but no hire events for a staffing agency
  subscribedEvents: isRecruiter.value ? [...SUPPLIER_WEBHOOK_EVENT_TYPES] : null,
  clientId: null,
  clientSecret: null,
  clientUrl: null,
  account: currentAccount.globalId,
})

const clean = { ...input } // Make a copy of a clean data sheet

watch(show, () => {
  Object.assign(input, clean) // Clean up our data
})

const {
  loading,
  mutate: createWebhook,
  error,
} = useMutation(createWebhookMutation, () => ({
  clientId,
  refetchQueries: ['Webhooks'],
}))

const clientRequire = () => input.secret === null
const secretRequire = () =>
  input.clientSecret === null && input.clientUrl === null && input.clientId === null

const apollo = useApolloError(error)
const vulidate = useCustomVuelidate(
  {
    title: { required },
    url: { required, url },
    clientUrl: { url, requiredIfClient: requiredIf(clientRequire) },
    clientSecret: { requiredIfClient: requiredIf(clientRequire) },
    clientId: { requiredIfClient: requiredIf(clientRequire) },
    secret: { requiredIfSecret: requiredIf(secretRequire) },
  },
  input,
)

const validationErrors = useApolloAndVuelidate(apollo, vulidate, 'input')

const submit = async () => {
  try {
    vulidate.touchAll()
    if (!vulidate.error.value) {
      const copy = { ...input }
      const secret = copy.secret ?? copy.clientSecret
      delete copy.clientSecret
      await createWebhook({
        input: {
          ...copy,
          secret,
        },
      })

      show.value = false
    }
  } catch (ex: any) {
    new NetworkError({ ex }).dispatch()
  }
}
</script>

<style scoped lang="scss"></style>
